import {
  AppShell,
  Group,
  Tabs,
  rem,
  Avatar,
  Text,
  Menu,
  ScrollArea,
  Divider,
  Title,
  Select,
  Button,
  Center,
  Loader,
} from "@mantine/core";
import {
  IconSettings,
  IconLogout,
  IconUser,
  IconContract,
  IconUsers,
  IconBuilding,
  IconMap2,
  IconShield,
  IconUserStar,
  IconUsersGroup,
  IconFileExport,
  IconWorld,
  IconMail,
  IconPalette,
  IconMapPin,
  IconTool,
} from "@tabler/icons-react";
import { useNavigate, useLocation } from "react-router";
import { usePermissions } from "../../hooks/usePermissions";
import { useState, useEffect, useRef } from "react";
import useTransientStore from "../../data/transcientStore";
import classes from "./styles.module.css";
import AdminPanel from "../adminPanel/AdminPanel";
import { useMsal } from "@azure/msal-react";
import { AUTH_ENABLED } from "../../data/authConfig";
import MapPanel from "../map/mapPanel";
import useStore from "../../data/store";
import {
  getStatusByAddressSystem,
  getQualityControlByAddressSystem,
} from "../../services/MapComponentsService";
import Feature from "ol/Feature";
import Geometry from "ol/geom/Geometry";
import { instantiateLegendTree, getNameInitials } from "../../utilities/Utilities";
import oneLogo from "../../assets/ramboll-one-icon.jpg";
import TranslationsPage from "../../components/management/TranslationsPage";
import useLanguageStore from "../../data/languageStore";
import {
  useTranslation,
  loadAllTranslationsToCache,
} from "../../hooks/useTranslation";
import { getClientSystemSettings } from "../../services/ClientSystemSettingsService";
import { allowedDevUsers } from "../../constants/devConstants";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const iconStyle = { width: rem(22), height: rem(22) };
  const username = useTransientStore((state) => state.currentUser);
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const currentUserEmail = useTransientStore((state) => state.currentUserEmail);
  const currentUsername = useTransientStore((state) => state.currentUsername);
  const currentUserPhotoUrl = useTransientStore((state) => state.currentUserPhotoUrl);
  const { checkAccess } = usePermissions();
  const currentUserRole = useTransientStore((state) => state.currentUserRole);
  // Admin for current address system (based on permission store)
  const isGlobalAdmin = currentUserRole === 1;
  const [devAdminEnabled, setDevAdminEnabled] = useState(false);
  const currentUserIdentifier = `${currentUserId}-${currentUserEmail}`;
  const isDevUser = allowedDevUsers.includes(currentUserIdentifier);
  const canAccessAdminTabs = isGlobalAdmin || (isDevUser && devAdminEnabled);
  const { instance: msalInstance } = useMsal();
  const setToken = useTransientStore((state) => state.setToken);
  const setClickedPolygonIDs = useStore((state) => state.setClickedPolygonIDs);
  const drawing = useTransientStore((state) => state.drawing);
  const treeData = useTransientStore((state) => state.locationTreeData);
  const setTreeData = useTransientStore((state) => state.setLocationTreeData);
  const currentLocation = useTransientStore((state) => state.currentLocation);
  const setCurrentLocation = useTransientStore(
    (state) => state.setCurrentLocation,
  );
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const currentUserAddressSystems = useTransientStore(
    (state) => state.currentUserAddressSystems,
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;
  const { getTranslation } = useTranslation({
    pageId: 336,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });
  const setSelectedAddressSystemStatuses = useTransientStore(
    (state) => state.setSelectedAddressSystemStatuses,
  );
  const setGeometrySearchFilters = useTransientStore(
    (state) => state.setGeometrySearchFilters,
  );
  const setGeometrySearchResults = useTransientStore(
    (state) => state.setGeometrySearchResults,
  );
  const setClientSystemSettings = useTransientStore(
    (state) => state.setClientSystemSettings,
  );
  const clientSystemSettings = useTransientStore(
    (state) => state.clientSystemSettings,
  );

  // Removed unused sidebar/menu disclosure state to reduce lint noise
  const map = useTransientStore((state) => state.map);

  const setSelectedAddressSystem = useTransientStore(
    (state) => state.setSelectedAddressSystem,
  );

  // Used by handleAddressSystemSwitch to clear geometries when switching address systems
  const setCurrentPolylines = useTransientStore(
    (state) => state.setCurrentPolylines,
  );
  const currentPolygons = useTransientStore((state) => state.currentPolygons);
  const setCurrentPolygons = useTransientStore(
    (state) => state.setCurrentPolygons,
  );
  const setCurrentPolypoints = useTransientStore(
    (state) => state.setCurrentPolypoints,
  );

  const projection = useStore((state) => state.projection);

  // Language selection state from store
  const selectedLanguage = useLanguageStore((state) => state.selectedLanguage);
  const setSelectedLanguageInStore = useLanguageStore(
    (state) => state.setSelectedLanguage,
  );
  const languages = useLanguageStore((state) => state.languages);
  const isTranslationsLoaded = useLanguageStore(
    (state) => state.isTranslationsLoaded,
  );
  const isTranslationsLoading = useLanguageStore(
    (state) => state.isTranslationsLoading,
  );

  // Persist mapoverview tab selection in URL
  function getTabFromQuery() {
    const params = new URLSearchParams(location.search);
    return params.get("tab") || "mapoverview";
  }
  const [activeTab, setActiveTab] = useState(getTabFromQuery());
  // Removed collapsible admin nav; always expanded when visible

  // Track whether client system settings have been loaded for current address system
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Admin tab state
  function getAdminTabFromQuery() {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get("adminTab");

    const validTabsForUsers = [
      "users",
      "organisations",
      "address_systems",
      "role_management",
      "export_config",
    ];
    const validTabsForContracts = [
      "contracts",
      "contract_users",
      "contract_default_users",
      "email_notification_settings",
    ];
    const validTabs =
      activeTab === "users" ? validTabsForUsers : validTabsForContracts;

    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      return tabFromUrl;
    }

    // Default based on dashboard type
    if (activeTab === "users") {
      return "users";
    } else if (activeTab === "contracts") {
      return "contracts";
    }

    return "users";
  }
  const [activeAdminTab, setActiveAdminTab] = useState(getAdminTabFromQuery());

  useEffect(() => {
    const newTab = getTabFromQuery();
    const adminTabs = ["users", "contracts", "user_roles", "translations"];

    // Don't reset from an admin tab to mapoverview when URL becomes empty
    // (this can happen during MSAL token refresh or other redirects)
    if (location.search === "" && adminTabs.includes(activeTab)) {
      // Restore the URL to match the current tab
      const params = new URLSearchParams();
      params.set("tab", activeTab);
      if (activeTab === "users" || activeTab === "contracts") {
        params.set("adminTab", activeAdminTab);
      }
      navigate({ search: params.toString() }, { replace: true });
      return;
    }

    setActiveTab(newTab);
    setActiveAdminTab(getAdminTabFromQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Initialize legend tree for map layer controls
  useEffect(() => {
    const legendData = instantiateLegendTree();
    setTreeData(legendData);
  }, [setTreeData]);

  // Set default map center location
  useEffect(() => {
    setCurrentLocation({
      id: 0,
      name: "Default Location",
      coordinates: [2857529.364488623, 8487719.103925403],
    });
  }, [setCurrentLocation]);

  /* Obsolete: Bulk geometry fetch on dashboard load - removed to improve initial page load performance. 
     Geometries are now loaded on-demand when needed by the map viewer.
  useEffect(() => {
    const fetchAllGeometries = async () => {
      setLoadingOverlayVisibility(true);
      if (selectedAddressSystem.id < 1) {
        setLoadingOverlayVisibility(false);
        navigate("/select-address-system");
        return;
      }

      try {
        // Fire all three bulk requests in parallel
        const [polylines, polygons, polypoints] = await Promise.all([
          getAllPolylinesByAddressSystem(selectedAddressSystem.id),
          getAllPolygonsByAddressSystem(selectedAddressSystem.id),
          getAllPolypointsByAddressSystem(selectedAddressSystem.id),
        ]);

        // setCurrentPolylines(polylines);
        // setCurrentPolygons(polygons);
        // setCurrentPolypoints(polypoints);

        const centerCoordinates = GetCentreCoordinates(
          `GEOMETRYCOLLECTION (${polylines[0].wkt}, ${polygons[0].wkt}, ${polypoints[0].wkt})`,
        );
        // Center default location (could be improved with actual extent calc later)
        setCurrentLocation({
          id: 0,
          name:
            getTranslation("LABEL_DASHBOARD_DEFAULT_LOCATION") ||
            "Default Location",
          coordinates: centerCoordinates
            ? centerCoordinates
            : [2857529.364488623, 8487719.103925403],
        });

        const legendTree = instantiateLegendTree();
        setTreeData(legendTree);
      } catch (e) {
        console.error(
          "Bulk geometry fetch failed, falling back to per-contract strategy.",
          e,
        );
        try {
          const addressSystemContracts = await getContractsByAddressSystem(
            selectedAddressSystem.id,
          );
          let polylines: RIMSPolyline[] = [];
          let polygons: Polygon[] = [];
          let polypoints: Point[] = [];
          for (const contract of addressSystemContracts) {
            const [contractPolylines, contractPolygons, contractPoints] =
              await Promise.all([
                getPolylines(selectedAddressSystem.id, contract.id),
                getPolygons(selectedAddressSystem.id, contract.id),
                getPolypoints(selectedAddressSystem.id, contract.id),
              ]);
            polylines = polylines.concat(contractPolylines);
            polygons = polygons.concat(contractPolygons);
            polypoints = polypoints.concat(contractPoints);
          }
          // setCurrentPolylines(polylines);
          // setCurrentPolygons(polygons);
          // setCurrentPolypoints(polypoints);
          setCurrentLocation({
            id: 0,
            name:
              getTranslation("LABEL_DASHBOARD_DEFAULT_LOCATION") ||
              "Default Location",
            coordinates: [2857529.364488623, 8487719.103925403],
          });
          instantiateLegendTree();
        } catch (error_) {
          console.error("Per-contract fallback also failed.", error_);
        }
      } finally {
        setLoadingOverlayVisibility(false);
      }
    };
    fetchAllGeometries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  */

  // Redirect non-admins away from admin-only tabs
  useEffect(() => {
    if (
      currentUserRole !== 0 && // Role from localStorage
      !canAccessAdminTabs &&
      (activeTab === "users" ||
        activeTab === "contracts" ||
        activeTab === "user_roles" ||
        activeTab === "translations")
    ) {
      const params = new URLSearchParams(location.search);
      params.set("tab", "mapoverview");
      params.delete("adminTab");
      navigate({ search: params.toString() }, { replace: true });
      setActiveTab("mapoverview");
    }
  }, [
    canAccessAdminTabs,
    activeTab,
    navigate,
    location.search,
    currentUserRole,
  ]);

  const handleTabChange = (value: string | null) => {
    if (!value) return;
    // Prevent non-admins from accessing admin-only tabs
    if ((value === "users" || value === "contracts") && !canAccessAdminTabs) {
      console.log("[handleTabChange] BLOCKED - not admin");
      return;
    }
    setActiveTab(value);
    const params = new URLSearchParams(location.search);
    params.set("tab", value);
    // Reset admin tab when changing Map Overview tab
    if (value === "users") {
      params.set("adminTab", "users");
      setActiveAdminTab("users");
    } else if (value === "contracts") {
      params.set("adminTab", "contracts");
      setActiveAdminTab("contracts");
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleAdminTabChange = (value: string | null) => {
    if (!value) return;
    setActiveAdminTab(value);
    const params = new URLSearchParams(location.search);
    params.set("adminTab", value);
    navigate({ search: params.toString() }, { replace: true });
  };

  const resetSession = useTransientStore((state) => state.resetSession);
  const handleLogout = async () => {
    // Clear client-side session state before redirect
    setToken(null);
    resetSession();

    // Clear all persisted stores from localStorage (industry standard)
    localStorage.removeItem("transient-store");
    localStorage.removeItem("language-store");
    localStorage.removeItem("currentLocation");

    // Clear security-sensitive sessionStorage
    sessionStorage.removeItem("permission-store");

    if (!AUTH_ENABLED) {
      navigate("/");
      return;
    }
    try {
      await msalInstance.logoutRedirect({
        account:
          msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0],
        postLogoutRedirectUri: "/",
        onRedirectNavigate: (url) => {
          // Force navigation handling by app router after logout to avoid blank screen
          return true; // allow default navigation
        },
      });
    } catch (e) {
      console.error("Logout redirect failed", e);
      // Fallback: navigate to root if redirect fails
      navigate("/");
    }
  };

  const handleLanguageChange = async (languageId: string | null) => {
    if (!languageId) return;

    const languageIdNum = Number.parseInt(languageId, 10);

    try {
      // Update store (this also persists to localStorage)
      setSelectedLanguageInStore(languageIdNum);
    } catch (error) {
      console.error("Error updating language:", error);
      alert(
        getTranslation("LABEL_DASHBOARD_LANGUAGE_UPDATE_FAILED") ||
          "Failed to update language",
      );
      // Revert to previous language on error
      setSelectedLanguageInStore(selectedLanguage);
    }
  };

  const handleAddressSystemSwitch = () => {
    setSelectedAddressSystem({ id: 0, name: "" });
    setSelectedAddressSystemStatuses([]);
    setClientSystemSettings(new Map<string, string>()); // Clear settings with correct type
    setGeometrySearchFilters({
      contractStatus: "active",
      selectedContractIds: [],
      geometryName: "",
      geometryType: "all",
      createdBy: "",
      foremanId: null,
      qualityControl1Id: null,
      qualityControl2Id: null,
      fromDate: null,
      toDate: null,
      geometryStatusId: null,
      sortBy: "name",
    });
    setGeometrySearchResults([]);

    // Clear geometry stores
    setCurrentPolylines([]);
    setCurrentPolypoints([]);
    setCurrentPolygons([]);
    globalThis.location.href = "/select-address-system";
  };

  useEffect(() => {
    // Allow global admins (role 1) or dev user with no address systems to stay on dashboard
    if (
      selectedAddressSystem.id < 1 &&
      !isGlobalAdmin &&
      !allowedDevUsers.includes(currentUserIdentifier)
    ) {
      navigate("/select-address-system");
    } else if (selectedAddressSystem.id > 0) {
      getStatusByAddressSystem(selectedAddressSystem.id).then((statuses) => {
        useTransientStore.getState().setSelectedAddressSystemStatuses(statuses);
      });
      getQualityControlByAddressSystem(selectedAddressSystem.id).then(
        (qualityControls) => {
          useTransientStore
            .getState()
            .setSelectedAddressSystemQualityControls(qualityControls);
        },
      );
    }
  }, [
    selectedAddressSystem,
    currentUserRole,
    navigate,
    currentUserIdentifier,
    isGlobalAdmin,
  ]);

  // Reload clientSystemSettings on page refresh if they're missing
  useEffect(() => {
    const loadClientSystemSettings = async () => {
      // Only attempt to load once per address system
      if (
        selectedAddressSystem.id > 0 &&
        clientSystemSettings.size === 0 &&
        !settingsLoaded
      ) {
        try {
          setSettingsLoaded(true); // Prevent re-attempts
          const settings = await getClientSystemSettings(
            selectedAddressSystem.id,
          );
          const settingsMap = new Map(Object.entries(settings));
          setClientSystemSettings(settingsMap);
        } catch (error) {
          console.error("Failed to reload client system settings:", error);
          // Even on error, mark as loaded to prevent retry loop
        }
      }
    };

    loadClientSystemSettings();
  }, [
    selectedAddressSystem.id,
    clientSystemSettings.size,
    settingsLoaded,
    setClientSystemSettings,
  ]);

  // Reset settingsLoaded flag when address system changes
  useEffect(() => {
    setSettingsLoaded(false);
  }, [selectedAddressSystem.id]);

  // Bulk load ALL translations for ALL languages once when address system changes
  // Also load for global admins or dev users with no address system (id=0) - they get common translations
  useEffect(() => {
    if (selectedAddressSystem.id > 0 || isGlobalAdmin || isDevUser) {
      loadAllTranslationsToCache(selectedAddressSystem.id).catch((err) => {
        console.error("[Dashboard] Failed to bulk load translations:", err);
      });
    }
  }, [selectedAddressSystem.id, isGlobalAdmin, isDevUser]);

  useEffect(() => {
    // Wait for translations to load before setting title
    if (!isTranslationsLoaded) return;

    if (activeTab === "mapoverview") {
      document.title =
        getTranslation("TITLE_RIMS_MAP_OVERVIEW") || "RIMS - Map Overview";
    } else if (activeTab === "users") {
      document.title = getTranslation("TITLE_RIMS_USERS") || "RIMS - Users";
    } else if (activeTab === "contracts") {
      document.title =
        getTranslation("TITLE_RIMS_CONTRACTS") || "RIMS - Contracts";
    }
    // else if (activeTab === "user_roles") {
    //     document.title = getTranslation("TITLE_RIMS_USER_ROLES") || "RIMS - User Roles";
    // }
    else {
      document.title = getTranslation("TITLE_RIMS") || "RIMS";
    }
  }, [activeTab, getTranslation, isTranslationsLoaded]);

  // Ref to support cycling through overlapping features on repeated clicks
  const overlapCycleRef = useRef<{
    pixel: [number, number] | null;
    features: Feature<Geometry>[];
    index: number;
    ts: number;
  }>({
    pixel: null,
    features: [],
    index: 0,
    ts: 0,
  });

  useEffect(() => {
    if (!map) return;

    const handleMapClick = (event: any) => {
      if (drawing) return;

      // Collect all features under cursor (supports nested polygons)
      const stacked: Feature<Geometry>[] = [];
      map.forEachFeatureAtPixel(event.pixel, (f: any) => {
        stacked.push(f as Feature<Geometry>);
      });

      if (!stacked.length) {
        setClickedPolygonIDs([]);
        overlapCycleRef.current = {
          pixel: null,
          features: [],
          index: 0,
          ts: Date.now(),
        };
        return;
      }

      // Determine if user is clicking same spot (cycle through overlaps)
      const last = overlapCycleRef.current;
      const now = Date.now();
      const samePixel =
        last.pixel &&
        Math.abs(last.pixel[0] - event.pixel[0]) +
          Math.abs(last.pixel[1] - event.pixel[1]) <=
          3; // Manhattan distance threshold
      const withinWindow = now - last.ts < 1200; // 1.2s window to cycle

      let chosen: Feature<Geometry> | null = null;

      if (samePixel && withinWindow && last.features.length > 1) {
        // Continue cycling through previously cached feature stack
        last.index = (last.index + 1) % last.features.length;
        chosen = last.features[last.index];
        last.ts = now;
      } else {
        // Fresh click: build ordered feature list with polygons first, sorted by area ascending (inner-most first)
        const polygons = stacked.filter((f) => {
          const type = f.getGeometry()?.getType();
          return type === "Polygon" || type === "MultiPolygon";
        });
        const others = stacked.filter((f) => !polygons.includes(f));
        polygons.sort((a, b) => {
          const aArea =
            (a.getGeometry() as any)?.getArea?.() ?? Number.POSITIVE_INFINITY;
          const bArea =
            (b.getGeometry() as any)?.getArea?.() ?? Number.POSITIVE_INFINITY;
          return aArea - bArea; // smallest first
        });
        const ordered = [...polygons, ...others];
        chosen = ordered[0];
        overlapCycleRef.current = {
          pixel: event.pixel.slice() as [number, number],
          features: ordered,
          index: 0,
          ts: now,
        };
      }

      if (chosen) {
        const clickedId = chosen.get("LocalID");
        setClickedPolygonIDs(clickedId == null ? [] : [clickedId]);
      } else {
        setClickedPolygonIDs([]);
      }
    };

    map.on("click", handleMapClick);
    return () => {
      map.un("click", handleMapClick);
    };
  }, [map, drawing, setClickedPolygonIDs]);

  // Config for admin section tabs
  const userAdminTabs = [
    {
      value: "users",
      key: "LABEL_DASHBOARD_USERS",
      label: getTranslation("LABEL_DASHBOARD_USERS") || "Users",
      icon: IconUsers,
    },
    {
      value: "organisations",
      key: "LABEL_DASHBOARD_ORGANISATIONS",
      label: getTranslation("LABEL_DASHBOARD_ORGANISATIONS") || "Organisations",
      icon: IconBuilding,
    },
    {
      value: "address_systems",
      key: "LABEL_DASHBOARD_ADDRESS_SYSTEMS",
      label:
        getTranslation("LABEL_DASHBOARD_ADDRESS_SYSTEMS") || "Address Systems",
      icon: IconMap2,
    },
    {
      value: "role_management",
      key: "LABEL_DASHBOARD_USER_ROLES",
      label: getTranslation("LABEL_DASHBOARD_USER_ROLES") || "User Roles",
      icon: IconShield,
    },
    {
      value: "export_config",
      key: "LABEL_DASHBOARD_EXPORT_CONFIG",
      label: getTranslation("LABEL_DASHBOARD_EXPORT_CONFIG") || "Export Config",
      icon: IconFileExport,
    },
  ];
  const contractAdminTabs = [
    {
      value: "contracts",
      key: "LABEL_DASHBOARD_CONTRACTS",
      label: getTranslation("LABEL_DASHBOARD_CONTRACTS") || "Contracts",
      icon: IconContract,
    },
    {
      value: "contract_users",
      key: "LABEL_DASHBOARD_CONTRACT_USERS",
      label:
        getTranslation("LABEL_DASHBOARD_CONTRACT_USERS") || "Contract Users",
      icon: IconUsersGroup,
    },
    {
      value: "contract_default_users",
      key: "LABEL_DASHBOARD_CONTRACT_DEFAULT_USERS",
      label:
        getTranslation("LABEL_DASHBOARD_CONTRACT_DEFAULT_USERS") ||
        "Contract Default Users",
      icon: IconUserStar,
    },
    {
      value: "email_notification_settings",
      key: "LABEL_DASHBOARD_EMAIL_NOTIFICATION_SETTINGS",
      label:
        getTranslation("LABEL_DASHBOARD_EMAIL_NOTIFICATION_SETTINGS") ||
        "Email Notification Settings",
      icon: IconMail,
    },
    {
      value: "status_colours",
      key: "LABEL_DASHBOARD_STATUS_COLOURS",
      label:
        getTranslation("LABEL_DASHBOARD_STATUS_COLOURS") || "Status Colours",
      icon: IconPalette,
    },
  ];

  const showAdminNav =
    canAccessAdminTabs && (activeTab === "users" || activeTab === "contracts");
  const navbarWidth = showAdminNav ? 300 : 0;

  // Block render until translations are loaded to prevent text code flash
  // Also applies to global admins or dev users with no address system (id=0)
  const shouldWaitForTranslations =
    selectedAddressSystem.id > 0 || isGlobalAdmin || isDevUser;
  // Bulk load is triggered only in useEffect (above) — never call loadAllTranslationsToCache during render;
  // it updates languageStore synchronously and causes "Cannot update X while rendering Y" warnings.

  if (
    shouldWaitForTranslations &&
    (isTranslationsLoading || !isTranslationsLoaded)
  ) {
    return (
      <Center style={{ width: "100vw", height: "100vh" }}>
        <Loader size={48} color="blue" type="bars" />
      </Center>
    );
  }

  const mapOverviewLabel = getTranslation("LABEL_DASHBOARD_MAP_OVERVIEW");
  const usersLabel = getTranslation("LABEL_DASHBOARD_USERS");
  const contractsLabel = getTranslation("LABEL_DASHBOARD_CONTRACTS");
  const translationsLabel = getTranslation("LABEL_DASHBOARD_TRANSLATIONS");

  const adminTabFontSize = (label: string | null | undefined, key: string) =>
    !label || label === key ? "10px" : undefined;

  const headerTabFontSize = (label: string | null | undefined, key: string) =>
    !label || label === key ? "10px" : "20px";

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: navbarWidth,
        breakpoint: "sm",
      }}
      padding={0}
      className={classes.appShellContainer}
    >
      <Tabs
        variant="pills"
        value={activeTab}
        onChange={handleTabChange}
        keepMounted={false}
      >
        <AppShell.Header>
          <Group
            h="100%"
            px="md"
            className={classes.header}
            justify="space-between"
          >
            <Group
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: "0 0 auto",
              }}
            >
              {/* <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
                            <Burger opened={desktopOpened} onClick={toggleDesktop} visibleFrom="sm" size="sm" /> */}
              <img
                src={oneLogo}
                alt="Ramboll Logo"
                style={{ height: 42, width: "auto", display: "block" }}
              />
              <Title
                order={1}
                ta="center"
                style={{ fontWeight: 700, letterSpacing: 1 }}
              >
                RIMS
              </Title>
              {selectedAddressSystem.id > 0 && (
                <Text size="lg" fw={500} c="dimmed">
                  {selectedAddressSystem.name}
                </Text>
              )}
            </Group>
            <Group
              visibleFrom="md"
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              <Tabs.List>
                {/* Dev Mode - only visible for dev users */}
                {isDevUser && !isGlobalAdmin && (
                  <Button
                    variant={devAdminEnabled ? "filled" : "subtle"}
                    color="orange"
                    leftSection={<IconTool size={20} />}
                    onClick={() => setDevAdminEnabled(!devAdminEnabled)}
                    style={{
                      fontSize: 16,
                      fontWeight: 500,
                      padding: "12px 20px",
                      borderRadius: 0,
                      height: "auto",
                    }}
                  >
                    Dev Mode
                  </Button>
                )}
                <Tabs.Tab
                  value="mapoverview"
                  leftSection={<IconMapPin style={iconStyle} />}
                  color="#009DF0"
                  style={{
                    fontSize: headerTabFontSize(
                      mapOverviewLabel,
                      "LABEL_DASHBOARD_MAP_OVERVIEW",
                    ),
                    fontWeight: 500,
                    padding: "12px 20px",
                  }}
                  className={classes.headerTab}
                >
                  {mapOverviewLabel || "Map Overview"}
                </Tabs.Tab>
                {canAccessAdminTabs && (
                  <Tabs.Tab
                    value="users"
                    leftSection={<IconUser style={iconStyle} />}
                    color="#009DF0"
                    style={{
                      fontSize: headerTabFontSize(
                        usersLabel,
                        "LABEL_DASHBOARD_USERS",
                      ),
                      fontWeight: 500,
                      padding: "12px 20px",
                    }}
                    className={classes.headerTab}
                  >
                    {usersLabel || "Users"}
                  </Tabs.Tab>
                )}
                {canAccessAdminTabs && (
                  <Tabs.Tab
                    value="contracts"
                    leftSection={<IconContract style={iconStyle} />}
                    color="#009DF0"
                    style={{
                      fontSize: headerTabFontSize(
                        contractsLabel,
                        "LABEL_DASHBOARD_CONTRACTS",
                      ),
                      fontWeight: 500,
                      padding: "12px 20px",
                    }}
                    className={classes.headerTab}
                  >
                    {contractsLabel || "Contracts"}
                  </Tabs.Tab>
                )}
                {canAccessAdminTabs && (
                  <Tabs.Tab
                    value="translations"
                    leftSection={<IconWorld style={iconStyle} />}
                    color="#009DF0"
                    style={{
                      fontSize: headerTabFontSize(
                        translationsLabel,
                        "LABEL_DASHBOARD_TRANSLATIONS",
                      ),
                      fontWeight: 500,
                      padding: "12px 20px",
                    }}
                    className={classes.headerTab}
                  >
                    {translationsLabel || "Translations"}
                  </Tabs.Tab>
                )}
              </Tabs.List>
            </Group>
            <Group
              align="right"
              className={classes["input-fields"]}
              style={{ flex: "0 0 auto" }}
            >
              {/* Language Selection Dropdown */}
              <Select
                leftSection={<IconWorld size={18} />}
                placeholder={
                  getTranslation("PLACEHOLDER_LANGUAGE") || "Language"
                }
                data={languages.map((lang) => ({
                  value: lang.id.toString(),
                  label: lang.name,
                }))}
                value={selectedLanguage.toString()}
                onChange={handleLanguageChange}
                searchable={false}
                clearable={false}
                style={{
                  minWidth: 140,
                }}
                classNames={{
                  input: classes.langSelectInput,
                  option: classes.langSelectOption,
                }}
                styles={{
                  input: {
                    backgroundColor: "#E0F0F8",
                    borderColor: "#b3d6e6",
                    color: "#063C55",
                    fontWeight: 500,
                  },
                  dropdown: {
                    background:
                      "linear-gradient(135deg,#E0F0F8 0%, #C9E4F2 100%)",
                    backdropFilter: "blur(4px)",
                    border: "1px solid #b3d6e6",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.16)",
                  },
                  option: {
                    color: "#063C55",
                    fontWeight: 500,
                    borderRadius: 6,
                    transition: "background 120ms ease, color 120ms ease",
                  },
                }}
              />
              <Menu
                withArrow
                classNames={{
                  item: classes.menuItem,
                }}
                styles={{
                  dropdown: {
                    background:
                      "linear-gradient(135deg,#E0F0F8 0%, #C9E4F2 100%)",
                    backdropFilter: "blur(4px)",
                    border: "1px solid #b3d6e6",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.16)",
                  },
                  arrow: {
                    background: "#E0F0F8",
                    borderColor: "#b3d6e6",
                  },
                  item: {
                    color: "#063C55",
                    fontWeight: 500,
                    borderRadius: 6,
                    transition: "background 120ms ease, color 120ms ease",
                  },
                  label: {
                    color: "#007AB8",
                    fontSize: 11,
                    letterSpacing: 0.5,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  },
                  divider: {
                    borderColor: "rgba(0,0,0,0.12)",
                  },
                }}
              >
                <Menu.Target>
                  <div className={classes.userBadge} title={`Username: ${currentUsername || username}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {currentUserPhotoUrl ? (
                      <Avatar 
                        src={currentUserPhotoUrl} 
                        size="sm" 
                      />
                    ) : (
                      <Avatar size="sm">
                        <IconUser size={18} />
                      </Avatar>
                    )}
                    <Text size="xs" fw={600}>{getNameInitials(username)}</Text>
                  </div>
                </Menu.Target>
                <Menu.Dropdown>
                  {username && (
                    <>
                      <Menu.Label>
                        {getTranslation("LABEL_DASHBOARD_SIGNED_IN_AS") ||
                          "Signed in as"}
                      </Menu.Label>
                      <Menu.Item disabled>
                        <Text fw={600} size="sm">
                          {username}
                        </Text>
                      </Menu.Item>
                      <Menu.Divider />
                    </>
                  )}
                  {(selectedAddressSystem.id > 0 ||
                    currentUserAddressSystems.length > 1) && (
                    <>
                      <Menu.Label>
                        {getTranslation("LABEL_DASHBOARD_APPLICATION") ||
                          "Application"}
                      </Menu.Label>
                      {selectedAddressSystem.id > 0 && (
                        <>
                          <Menu.Item disabled>
                            <Text size="xs" c="dimmed">
                              {getTranslation(
                                "LABEL_DASHBOARD_CURRENT_ADDRESS_SYSTEM",
                              ) || "Current Address System:"}
                            </Text>
                            <Text fw={600} size="sm">
                              {selectedAddressSystem.name}
                            </Text>
                          </Menu.Item>
                          <Menu.Divider />
                        </>
                      )}
                      {currentUserAddressSystems.length > 1 && (
                        <>
                          <Menu.Item
                            leftSection={<IconSettings size={14} />}
                            onClick={handleAddressSystemSwitch}
                          >
                            {getTranslation(
                              "LABEL_DASHBOARD_SWITCH_ADDRESS_SYSTEM",
                            ) || "Switch Address System"}
                          </Menu.Item>
                          <Menu.Divider />
                        </>
                      )}
                    </>
                  )}
                  <Menu.Label>
                    {getTranslation("LABEL_DASHBOARD_LOGOUT") || "Logout"}
                  </Menu.Label>
                  <Menu.Item
                    leftSection={<IconLogout size={20} />}
                    onClick={handleLogout}
                  >
                    {getTranslation("LABEL_DASHBOARD_LOGOUT") || "Logout"}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar
          p={0}
          style={{
            display: showAdminNav ? "flex" : "none",
            flexDirection: "column",
            background: "#fff",
            borderRight: "1px solid #e0e0e0",
          }}
        >
          {showAdminNav && (
            <>
              <Group px="sm" py={10} style={{ flexShrink: 0 }}>
                <Text fw={600} size="sm">
                  {getTranslation("LABEL_DASHBOARD_ADMINISTRATION") ||
                    "Administration"}
                </Text>
              </Group>
              <Divider />
              <Tabs
                orientation="vertical"
                variant="pills"
                value={activeAdminTab}
                onChange={handleAdminTabChange}
                keepMounted={false}
                classNames={{
                  tab: classes.adminTab,
                }}
                styles={{
                  list: { width: "100%", gap: 4, padding: "8px 8px 12px 8px" },
                  tab: {
                    width: "100%",
                    justifyContent: "flex-start",
                    padding: "10px 14px",
                    borderRadius: 8,
                    marginBottom: 4,
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: 500,
                  },
                }}
                style={{ flex: 1, display: "flex", flexDirection: "column" }}
              >
                <ScrollArea
                  offsetScrollbars
                  scrollbarSize={8}
                  style={{ flex: 1 }}
                >
                  <Tabs.List>
                    {(activeTab === "users"
                      ? userAdminTabs
                      : contractAdminTabs
                    ).map((t) => (
                      <Tabs.Tab
                        key={t.value}
                        value={t.value}
                        leftSection={<t.icon size={16} />}
                      >
                        <Text
                          fw={500}
                          style={{
                            fontSize: adminTabFontSize(t.label, t.key || ""),
                          }}
                        >
                          {t.label}
                        </Text>
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>
                </ScrollArea>
              </Tabs>
            </>
          )}
        </AppShell.Navbar>

        <AppShell.Main className={classes.mainContent}>
          <Tabs.Panel value="mapoverview" className={classes.tabPanel}>
            {currentLocation && (
              <MapPanel
                polygonSections={currentPolygons}
                treeData={treeData}
                setTreeData={setTreeData}
                currentLocation={currentLocation}
                projection={projection}
              />
            )}
          </Tabs.Panel>
          {canAccessAdminTabs && (
            <Tabs.Panel value="users" className={classes.tabPanel}>
              <AdminPanel
                dashboardType="users"
                activeAdminTab={activeAdminTab}
                onAdminTabChange={handleAdminTabChange}
              />
            </Tabs.Panel>
          )}
          {canAccessAdminTabs && (
            <Tabs.Panel value="contracts" className={classes.tabPanel}>
              <AdminPanel
                dashboardType="contracts"
                activeAdminTab={activeAdminTab}
                onAdminTabChange={handleAdminTabChange}
              />
            </Tabs.Panel>
          )}
          {canAccessAdminTabs && (
            <Tabs.Panel value="translations">
              <TranslationsPage />
            </Tabs.Panel>
          )}
        </AppShell.Main>
      </Tabs>
    </AppShell>
  );
}
